import { until } from "@vueuse/core";

import type { MaybeRefOrGetter } from "vue";
import { toValue } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useAuthenticationStore } from "@/stores/authentication-store";

import { createClient as createFetchClient } from "./gen/client/client.gen";
import { type Client } from "./gen/client/types.gen";
import { threadRevisionContext } from "./thread-revision-context";

const DEFAULT_BACKEND_URI = import.meta.env.VITE_API_URL;

export interface ServiceOpts {
    requires: {
        auth: boolean;
        organization: boolean;
        stek: boolean;
    };
    overrides?: {
        organizationId?: string;
        stekId?: string;
    };
}

interface ClientOpts extends ServiceOpts {
    backendURI?: MaybeRefOrGetter<string>;
}

export function createClient(opts?: ClientOpts): Client {
    return createFetchClient({
        baseUrl: toValue(opts?.backendURI || DEFAULT_BACKEND_URI),
        // "same-origin" instead of "omit": auth stays bearer-based, but proxied
        // preview sessions (dev-agent live preview) authorize by a cookie that
        // must accompany same-origin API calls. No cross-origin cookies either way.
        credentials: "same-origin",
    });
}

export const useClient = (opts: ClientOpts) => {
    const client = createClient(opts);

    // Requests authorized with the boot-hint token (served from Firebase's
    // IndexedDB before the SDK settles — see lib/firebase-token-hint.ts) get
    // a pre-fetch clone stashed here so a 401 can be retried once with the
    // settled token. Entries die with their Request.
    const bootHintRetries = new WeakMap<Request, Request>();

    client.interceptors.request.use(async (request) => {
        // It's important these are called inside the interceptor to work.
        const { getToken, isAuthSettled } = useAuthenticationStore();
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        let usedBootHint = false;
        if (opts.requires.auth) {
            const token = await getToken();
            if (!token) {
                throw Error("Request requires auth, but token not available.");
            }
            request.headers.set("Authorization", `Bearer ${token}`);
            // getToken only returns while auth is still unsettled when it
            // served the boot hint.
            usedBootHint = !isAuthSettled();
        }

        if (opts.requires.organization) {
            let organization_id = opts.overrides?.organizationId;
            if (!organization_id) {
                await until(organization).toMatch((v) => v !== undefined, {
                    timeout: 5000,
                });
                organization_id = organization.value?.identity;
            }
            if (!organization_id) {
                throw Error(
                    "Request requires organization, but organization not available."
                );
            }
            request.headers.set("Organization", organization_id);
        }

        if (opts.requires.stek) {
            let stek_id = opts.overrides?.stekId;
            if (!stek_id) {
                await until(stek).toMatch((v) => v !== undefined, {
                    timeout: 10000,
                });
                stek_id = stek.value?.identity;
            }
            if (!stek_id) {
                throw Error("Request requires stek, but stek not available.");
            }
            request.headers.set("Stek", stek_id);
        }

        // read-revision-as-context: when the UI is viewing a thread branch, send
        // it so the backend resolves reads at that branch without a per-endpoint
        // `revision` parameter (see infrastructure._read_thread_revision_header).
        // Read from a leaf holder the release-store keeps in sync — importing the
        // store here would create a module-load cycle (client → release-store →
        // lib/openapi → client). Only thread/<id> revisions are set; releases and
        // the working copy leave it null.
        const threadRevision = threadRevisionContext.current;
        if (threadRevision) {
            request.headers.set("x-thread-revision", threadRevision);
        }

        if (usedBootHint) {
            // Clone after all headers are set so the retry replays the
            // exact request (minus the stale Authorization).
            bootHintRetries.set(request, request.clone());
        }

        return request;
    });

    // Add response interceptor to handle authentication and authorization errors
    client.interceptors.response.use(async (response, request) => {
        // A request fired with the boot-hint token lost the race against the
        // SDK's session validation (expired or revoked since persisted).
        // Retry exactly once with the settled token.
        const retry = bootHintRetries.get(request);
        if (response.status === 401 && retry) {
            bootHintRetries.delete(request);
            const { getToken } = useAuthenticationStore();
            const settledToken = await getToken({ allowBootHint: false });
            if (
                settledToken &&
                `Bearer ${settledToken}` !== retry.headers.get("Authorization")
            ) {
                retry.headers.set("Authorization", `Bearer ${settledToken}`);
                return await fetch(retry);
            }
        }

        // Handle 401 and 403 errors globally
        if (response.status === 403) {
            // Clone the response before reading the body to avoid consuming it
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json().catch(() => null);

            // Set a global unauthorized state
            window.dispatchEvent(
                new CustomEvent("unauthorized-access", {
                    detail: {
                        status: response.status,
                        message:
                            errorData?.detail?.[0]?.message ||
                            "You don't have permission to access this resource",
                    },
                })
            );
        }
        return response;
    });

    return client;
};
